import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Grid, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Dialog,
  DialogTitle, DialogContent, DialogActions, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, TableSortLabel
} from "@mui/material";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
  ComposedChart, Line
} from "recharts";
import { db } from "../firebase";
import { collection, onSnapshot, doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

// Il tracciamento costi (collection "costi", vedi CostiEvento.js) è stato introdotto a luglio
// 2026: gli eventi precedenti non hanno dati di costo affidabili/completi, quindi il grafico
// costi/ricavi parte da qui invece che dall'inizio dello storico eventi.
const DATA_INIZIO_COSTI_RICAVI = new Date("2026-07-01");
const TARIFFE_DEFAULT = { tariffeOrariaAlgores: 40, tariffeOrariaCollaboratori: 15 };

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu",
               "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const GIORNI = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

const COLORI = [
  "#1A56A0", "#3B8BD4", "#4CAF50", "#FF9800",
  "#E91E63", "#9C27B0", "#00BCD4", "#FF5722",
  "#607D8B", "#795548"
];

const COLORE_A = "#1A56A0";
const COLORE_B = "#F59E0B";

function getAnno(evento) {
  const data = evento.dataInizio || evento.data || evento.giornate?.[0]?.dataInizio;
  if (!data) return null;
  return new Date(data).getFullYear();
}

function getMese(evento) {
  const data = evento.dataInizio || evento.data || evento.giornate?.[0]?.dataInizio;
  if (!data) return null;
  return new Date(data).getMonth();
}

function getGiorno(evento) {
  const data = evento.dataInizio || evento.data || evento.giornate?.[0]?.dataInizio;
  if (!data) return null;
  const g = new Date(data).getDay();
  return g === 0 ? 6 : g - 1;
}

function getDataEvento(evento) {
  return evento.dataInizio || evento.data || evento.giornate?.[0]?.dataInizio || null;
}

function formatEuro(value) {
  return `€ ${Number(value).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
}

export default function Statistiche() {
  const navigate = useNavigate();
  const [eventi, setEventi] = useState([]);
  const [clientiMap, setClientiMap] = useState({});
  const [costi, setCosti] = useState([]);
  const [tariffe, setTariffe] = useState(TARIFFE_DEFAULT);
  const [loading, setLoading] = useState(true);

  const [annoSelezionato, setAnnoSelezionato] = useState("tutti");
  const [meseSelezionato, setMeseSelezionato] = useState("tutti");
  const [clienteSelezionato, setClienteSelezionato] = useState("tutti");
  const [annoA, setAnnoA] = useState("tutti");
  const [annoB, setAnnoB] = useState("tutti");
  const [vistaIntroiti, setVistaIntroiti] = useState("mese");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitolo, setDialogTitolo] = useState("");
  const [dialogEventi, setDialogEventi] = useState([]);
  const [dialogOrdinamento, setDialogOrdinamento] = useState({ campo: "dataInizio", direzione: "asc" });

  useEffect(() => {
    const unsubClienti = onSnapshot(collection(db, "clienti"), (snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data().nome; });
      setClientiMap(map);
    });
    const unsubEventi = onSnapshot(collection(db, "eventi"), (snap) => {
      const eventiCaricati = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((e) => !["chiusoPerso"].includes(e.stato));
      setEventi(eventiCaricati);
      setLoading(false);
    });
    const unsubCosti = onSnapshot(collection(db, "costi"), (snap) => {
      setCosti(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    getDoc(doc(db, "impostazioni", "tariffe")).then((snap) => {
      if (snap.exists()) setTariffe(snap.data());
    });
    return () => { unsubClienti(); unsubEventi(); unsubCosti(); };
  }, []);

  const anni = [...new Set(eventi.map(getAnno).filter(Boolean))].sort();

  const clientiUnici = [
    ...new Set(eventi.map((e) => clientiMap[e.clienteId] || e.clienteNomeStorico).filter(Boolean))
  ].sort();

  const eventiFiltrati = eventi.filter((e) => {
    const matchAnno = annoSelezionato === "tutti" || getAnno(e) === parseInt(annoSelezionato);
    const matchMese = meseSelezionato === "tutti" || getMese(e) === parseInt(meseSelezionato);
    const nomeCliente = clientiMap[e.clienteId] || e.clienteNomeStorico || "";
    const matchCliente = clienteSelezionato === "tutti" || nomeCliente === clienteSelezionato;
    return matchAnno && matchMese && matchCliente;
  });

  const eventiA = eventi.filter((e) => {
    const matchAnno = annoA === "tutti" || getAnno(e) === parseInt(annoA);
    const nomeCliente = clientiMap[e.clienteId] || e.clienteNomeStorico || "";
    const matchCliente = clienteSelezionato === "tutti" || nomeCliente === clienteSelezionato;
    return matchAnno && matchCliente;
  });

  const eventiB = eventi.filter((e) => {
    const matchAnno = annoB === "tutti" || getAnno(e) === parseInt(annoB);
    const nomeCliente = clientiMap[e.clienteId] || e.clienteNomeStorico || "";
    const matchCliente = clienteSelezionato === "tutti" || nomeCliente === clienteSelezionato;
    return matchAnno && matchCliente;
  });

  const keyA = annoA === "tutti" ? "Tutti A" : String(annoA);
  const keyB = annoB === "tutti" ? "Tutti B" : String(annoB);
  const labelA = annoA === "tutti" ? "Tutti gli anni" : String(annoA);
  const labelB = annoB === "tutti" ? "Tutti gli anni" : String(annoB);

  const datiMensiliConfronto = MESI.map((mese, idx) => ({
    mese,
    [keyA]: eventiA.filter((e) => getMese(e) === idx).length,
    [keyB]: eventiB.filter((e) => getMese(e) === idx).length,
  }));

  const datiIntroitiMensiliConfronto = MESI.map((mese, idx) => ({
    mese,
    [keyA]: eventiA.filter((e) => getMese(e) === idx).reduce((acc, e) => acc + (parseFloat(e.importoVendita) || 0), 0),
    [keyB]: eventiB.filter((e) => getMese(e) === idx).reduce((acc, e) => acc + (parseFloat(e.importoVendita) || 0), 0),
  }));

  const introitiAnniMap = {};
  eventi.filter((e) => {
    const nomeCliente = clientiMap[e.clienteId] || e.clienteNomeStorico || "";
    return clienteSelezionato === "tutti" || nomeCliente === clienteSelezionato;
  }).forEach((e) => {
    const anno = getAnno(e);
    if (anno) {
      if (!introitiAnniMap[anno]) introitiAnniMap[anno] = { eventi: 0, importo: 0 };
      introitiAnniMap[anno].importo += parseFloat(e.importoVendita) || 0;
      introitiAnniMap[anno].eventi += 1;
    }
  });
  const datiAnni = Object.entries(introitiAnniMap)
    .map(([anno, v]) => ({ anno, eventi: v.eventi, importo: v.importo }))
    .sort((a, b) => a.anno - b.anno);

  const datiGiorni = GIORNI.map((giorno, idx) => ({
    giorno,
    eventi: eventiFiltrati.filter((e) => getGiorno(e) === idx).length,
  }));

  const clientiCount = {};
  eventiFiltrati.forEach((e) => {
    const nome = clientiMap[e.clienteId] || e.clienteNomeStorico || "Sconosciuto";
    clientiCount[nome] = (clientiCount[nome] || 0) + 1;
  });
  const datiClienti = Object.entries(clientiCount)
    .map(([nome, count]) => ({ nome, eventi: count }))
    .sort((a, b) => b.eventi - a.eventi);

  const datiOreMensili = MESI.map((mese, idx) => ({
    mese,
    "Ore Algores": eventiFiltrati.filter((e) => getMese(e) === idx)
      .reduce((acc, e) => acc + (parseFloat(e.oreAlgores) || 0), 0),
    "Ore Collaboratori": eventiFiltrati.filter((e) => getMese(e) === idx)
      .reduce((acc, e) => acc + (parseFloat(e.oreCollaboratori) || 0), 0),
  }));

  // COSTI/RICAVI — stesso calcolo di src/components/AnalisiCostiRicavi.js (costi collection +
  // costi aggiuntivi manuali + costo orario Algores/Collaboratori da impostazioni/tariffe),
  // ma solo per eventi da DATA_INIZIO_COSTI_RICAVI in poi: prima di allora i costi non erano
  // tracciati sistematicamente, quindi il margine risulterebbe artificiosamente alto.
  const costiPerEvento = {};
  costi.forEach((c) => {
    costiPerEvento[c.eventoId] = (costiPerEvento[c.eventoId] || 0) + (parseFloat(c.importo) || 0);
  });

  const meseCostiRicaviMap = {};
  eventi.forEach((e) => {
    const nomeCliente = clientiMap[e.clienteId] || e.clienteNomeStorico || "";
    if (clienteSelezionato !== "tutti" && nomeCliente !== clienteSelezionato) return;
    const dataRaw = getDataEvento(e);
    if (!dataRaw) return;
    const data = new Date(dataRaw);
    if (isNaN(data) || data < DATA_INIZIO_COSTI_RICAVI) return;

    const ricavi = parseFloat(e.importoVendita) || 0;
    const costiAggiuntivi = parseFloat(e.costiAggiuntivi) || 0;
    const costoAlgores = (parseFloat(e.oreAlgores) || 0) * tariffe.tariffeOrariaAlgores;
    const costoCollaboratori = (parseFloat(e.oreCollaboratori) || 0) * tariffe.tariffeOrariaCollaboratori;
    const totaleCostiEvento = (costiPerEvento[e.id] || 0) + costiAggiuntivi + costoAlgores + costoCollaboratori;

    const chiave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, "0")}`;
    if (!meseCostiRicaviMap[chiave]) meseCostiRicaviMap[chiave] = { chiave, ricavi: 0, costi: 0, eventi: [] };
    meseCostiRicaviMap[chiave].ricavi += ricavi;
    meseCostiRicaviMap[chiave].costi += totaleCostiEvento;
    meseCostiRicaviMap[chiave].eventi.push(e);
  });

  const datiCostiRicavi = Object.values(meseCostiRicaviMap)
    .sort((a, b) => a.chiave.localeCompare(b.chiave))
    .map((v) => {
      const [anno, mm] = v.chiave.split("-");
      return { ...v, mese: `${MESI[parseInt(mm, 10) - 1]} ${anno}`, margine: v.ricavi - v.costi };
    });

  const totaleEventi = eventiFiltrati.length;
  const totaleFatturato = eventiFiltrati.reduce((acc, e) => acc + (parseFloat(e.importoVendita) || 0), 0);
  const mediaPerMese = (totaleEventi / 12).toFixed(1);

  function apriDettaglio(titolo, eventiDettaglio) {
    setDialogTitolo(titolo);
    setDialogEventi(eventiDettaglio);
    setDialogOrdinamento({ campo: "dataInizio", direzione: "asc" });
    setDialogOpen(true);
  }

  const CustomTooltipMensile = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="caption" fontWeight="bold">{label}</Typography>
          {payload.map((p) => (
            <Typography key={p.name} variant="caption" display="block" color={p.fill}>
              {p.name}: {p.value}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary">Clicca per dettaglio</Typography>
        </Paper>
      );
    }
    return null;
  };

  const CustomTooltipIntroiti = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="caption" fontWeight="bold">{label}</Typography>
          {payload.map((p) => (
            <Typography key={p.name} variant="caption" display="block" color={p.fill}>
              {p.name}: {formatEuro(p.value)}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary">Clicca per dettaglio</Typography>
        </Paper>
      );
    }
    return null;
  };

  const CustomTooltipCostiRicavi = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5 }}>
          <Typography variant="caption" fontWeight="bold">{label}</Typography>
          {payload.map((p) => (
            <Typography key={p.name} variant="caption" display="block" color={p.color}>
              {p.name}: {formatEuro(p.value)}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary">Clicca per dettaglio</Typography>
        </Paper>
      );
    }
    return null;
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

  return (
    <Box>
      {/* FILTRI */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
        <Typography variant="h5" fontWeight="bold" flex={1}>Statistiche</Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Anno</InputLabel>
          <Select value={annoSelezionato} label="Anno"
            onChange={(e) => { setAnnoSelezionato(e.target.value); setMeseSelezionato("tutti"); }}>
            <MenuItem value="tutti">Tutti gli anni</MenuItem>
            {anni.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </FormControl>
        {annoSelezionato !== "tutti" && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Mese</InputLabel>
            <Select value={meseSelezionato} label="Mese"
              onChange={(e) => setMeseSelezionato(e.target.value)}>
              <MenuItem value="tutti">Tutti i mesi</MenuItem>
              {MESI.map((m, idx) => <MenuItem key={idx} value={idx}>{m}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Cliente</InputLabel>
          <Select value={clienteSelezionato} label="Cliente"
            onChange={(e) => setClienteSelezionato(e.target.value)}>
            <MenuItem value="tutti">Tutti i clienti</MenuItem>
            {clientiUnici.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* KPI */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#E6F1FB", cursor: "pointer" }}
            onClick={() => apriDettaglio("Tutti gli eventi", eventiFiltrati)}>
            <Typography variant="h3" fontWeight="bold" color="primary">{totaleEventi}</Typography>
            <Typography variant="body2" color="text.secondary">Totale eventi</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#EAF3DE" }}>
            <Typography variant="h3" fontWeight="bold" color="success.main">
              € {totaleFatturato.toLocaleString("it-IT", { maximumFractionDigits: 0 })}
            </Typography>
            <Typography variant="body2" color="text.secondary">Fatturato totale</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 3, textAlign: "center", backgroundColor: "#FAEEDA" }}>
            <Typography variant="h3" fontWeight="bold" color="warning.main">{mediaPerMese}</Typography>
            <Typography variant="body2" color="text.secondary">Media eventi/mese</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* DISTRIBUZIONE MENSILE EVENTI */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight="bold">Distribuzione mensile eventi</Typography>
          <Box display="flex" gap={1}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Periodo A</InputLabel>
              <Select value={annoA} label="Periodo A" onChange={(e) => setAnnoA(e.target.value)}>
                <MenuItem value="tutti">Tutti</MenuItem>
                {anni.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Periodo B</InputLabel>
              <Select value={annoB} label="Periodo B" onChange={(e) => setAnnoB(e.target.value)}>
                <MenuItem value="tutti">Tutti</MenuItem>
                {anni.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={datiMensiliConfronto}
            onClick={(data) => {
              if (data?.activeLabel) {
                const mIdx = MESI.indexOf(data.activeLabel);
                const evDettaglio = eventi.filter((e) => {
                  const matchMese = getMese(e) === mIdx;
                  const annoEvento = getAnno(e);
                  const matchPeriodo = annoA === "tutti" || annoEvento === parseInt(annoA) ||
                                       annoB === "tutti" || annoEvento === parseInt(annoB);
                  const nomeCliente = clientiMap[e.clienteId] || e.clienteNomeStorico || "";
                  const matchCliente = clienteSelezionato === "tutti" || nomeCliente === clienteSelezionato;
                  return matchMese && matchPeriodo && matchCliente;
                });
                apriDettaglio(`${data.activeLabel} — eventi`, evDettaglio);
              }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="mese" />
            <YAxis allowDecimals={false} />
            <Tooltip content={<CustomTooltipMensile />} />
            <Legend />
            <Bar dataKey={keyA} name={labelA} fill={COLORE_A} radius={[4, 4, 0, 0]} />
            <Bar dataKey={keyB} name={labelB} fill={COLORE_B} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* INTROITI */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h6" fontWeight="bold">Introiti</Typography>
          <Box display="flex" gap={1}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Vista</InputLabel>
              <Select value={vistaIntroiti} label="Vista" onChange={(e) => setVistaIntroiti(e.target.value)}>
                <MenuItem value="mese">Per mese</MenuItem>
                <MenuItem value="anno">Per anno</MenuItem>
              </Select>
            </FormControl>
            {vistaIntroiti === "mese" && (
              <>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Periodo A</InputLabel>
                  <Select value={annoA} label="Periodo A" onChange={(e) => setAnnoA(e.target.value)}>
                    <MenuItem value="tutti">Tutti</MenuItem>
                    {anni.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Periodo B</InputLabel>
                  <Select value={annoB} label="Periodo B" onChange={(e) => setAnnoB(e.target.value)}>
                    <MenuItem value="tutti">Tutti</MenuItem>
                    {anni.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </Box>
        <ResponsiveContainer width="100%" height={280}>
          {vistaIntroiti === "mese" ? (
            <BarChart data={datiIntroitiMensiliConfronto}
              onClick={(data) => {
                if (data?.activeLabel) {
                  const mIdx = MESI.indexOf(data.activeLabel);
                  const evDettaglio = eventi.filter((e) => {
                    const matchMese = getMese(e) === mIdx;
                    const annoEvento = getAnno(e);
                    const matchPeriodo = annoA === "tutti" || annoEvento === parseInt(annoA) ||
                                         annoB === "tutti" || annoEvento === parseInt(annoB);
                    const nomeCliente = clientiMap[e.clienteId] || e.clienteNomeStorico || "";
                    const matchCliente = clienteSelezionato === "tutti" || nomeCliente === clienteSelezionato;
                    return matchMese && matchPeriodo && matchCliente;
                  });
                  apriDettaglio(`${data.activeLabel} — introiti`, evDettaglio);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mese" />
              <YAxis tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltipIntroiti />} />
              <Legend />
              <Bar dataKey={keyA} name={labelA} fill={COLORE_A} radius={[4, 4, 0, 0]} />
              <Bar dataKey={keyB} name={labelB} fill={COLORE_B} radius={[4, 4, 0, 0]} />
            </BarChart>
          ) : (
            <BarChart data={datiAnni}
              onClick={(data) => {
                if (data?.activeLabel) {
                  const evAnno = eventiFiltrati.filter((e) => String(getAnno(e)) === String(data.activeLabel));
                  apriDettaglio(`${data.activeLabel} — introiti`, evAnno);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="anno" />
              <YAxis tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatEuro(value)} />
              <Bar dataKey="importo" fill={COLORE_A} radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </Paper>

      {/* COSTI/RICAVI */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold">Costi / Ricavi</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Disponibile solo per eventi da luglio 2026 in poi, da quando i costi vengono tracciati sistematicamente.
        </Typography>
        {datiCostiRicavi.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            Nessun evento da luglio 2026 in poi con dati di costo/ricavo.
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={datiCostiRicavi}
              onClick={(data) => {
                if (data?.activeLabel) {
                  const voce = datiCostiRicavi.find((v) => v.mese === data.activeLabel);
                  apriDettaglio(`${data.activeLabel} — costi/ricavi`, voce?.eventi || []);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mese" />
              <YAxis tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltipCostiRicavi />} />
              <Legend />
              <Bar dataKey="ricavi" name="Ricavi" fill="#4CAF50" radius={[4, 4, 0, 0]} cursor="pointer" />
              <Bar dataKey="costi" name="Costi" fill="#E91E63" radius={[4, 4, 0, 0]} cursor="pointer" />
              <Line type="monotone" dataKey="margine" name="Margine" stroke="#1A56A0" strokeWidth={2} dot />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Paper>

      <Grid container spacing={3} mb={3}>
        {/* PER GIORNO */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>Per giorno della settimana</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={datiGiorni}
                onClick={(data) => {
                  if (data?.activeLabel) {
                    const gIdx = GIORNI.indexOf(data.activeLabel);
                    apriDettaglio(`${data.activeLabel} — eventi`, eventiFiltrati.filter((e) => getGiorno(e) === gIdx));
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="giorno" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="eventi" fill="#3B8BD4" radius={[4, 4, 0, 0]} cursor="pointer" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* PER ANNO */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" mb={2}>Per anno</Typography>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={datiAnni}
                onClick={(data) => {
                  if (data?.activeLabel) {
                    const evAnno = eventiFiltrati.filter((e) => String(getAnno(e)) === String(data.activeLabel));
                    apriDettaglio(`${data.activeLabel} — eventi`, evAnno);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="anno" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="eventi" fill="#4CAF50" radius={[4, 4, 0, 0]} cursor="pointer" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* ORE ALGORES E COLLABORATORI */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>Ore Algores e Collaboratori</Typography>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart
            data={datiOreMensili}
            onClick={(data) => {
              if (data?.activeLabel) {
                const evDettaglio = eventiFiltrati.filter((e) => MESI[getMese(e)] === data.activeLabel);
                apriDettaglio(`${data.activeLabel} — ore`, evDettaglio);
              }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="mese" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="Ore Algores" fill="#1A56A0" radius={[4, 4, 0, 0]} cursor="pointer" />
            <Bar dataKey="Ore Collaboratori" fill="#F59E0B" radius={[4, 4, 0, 0]} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* PER CLIENTE */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>Distribuzione per cliente</Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={datiClienti} dataKey="eventi" nameKey="nome"
                  cx="50%" cy="50%" outerRadius={110}
                  label={({ nome, percent }) => `${nome} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                  onClick={(data) => apriDettaglio(`${data.nome} — eventi`,
                    eventiFiltrati.filter((e) => (clientiMap[e.clienteId] || e.clienteNomeStorico) === data.nome)
                  )}
                  cursor="pointer"
                >
                  {datiClienti.map((_, idx) => (
                    <Cell key={idx} fill={COLORI[idx % COLORI.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} eventi`]} />
              </PieChart>
            </ResponsiveContainer>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              {datiClienti.map((c, idx) => (
                <Box key={c.nome} display="flex" alignItems="center" gap={1} mb={1}
                  sx={{ cursor: "pointer", "&:hover": { opacity: 0.7 } }}
                  onClick={() => apriDettaglio(`${c.nome} — eventi`,
                    eventiFiltrati.filter((e) => (clientiMap[e.clienteId] || e.clienteNomeStorico) === c.nome)
                  )}
                >
                  <Box sx={{
                    width: 14, height: 14, borderRadius: "50%",
                    backgroundColor: COLORI[idx % COLORI.length], flexShrink: 0
                  }} />
                  <Typography variant="body2" flex={1}>{c.nome}</Typography>
                  <Typography variant="body2" fontWeight="bold">{c.eventi}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* DIALOG DETTAGLIO */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitolo}</DialogTitle>
        <DialogContent>
          {dialogEventi.length === 0 ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              Nessun evento trovato.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                    {[
                      { id: "titolo", label: "Titolo" },
                      { id: "cliente", label: "Cliente" },
                      { id: "data", label: "Data" },
                      { id: "stato", label: "Stato" },
                      { id: "importo", label: "Importo", align: "right" },
                    ].map((col) => (
                      <TableCell key={col.id} align={col.align || "left"}>
                        <TableSortLabel
                          active={dialogOrdinamento.campo === col.id}
                          direction={dialogOrdinamento.campo === col.id ? dialogOrdinamento.direzione : "asc"}
                          onClick={() => setDialogOrdinamento((prev) => ({
                            campo: col.id,
                            direzione: prev.campo === col.id && prev.direzione === "asc" ? "desc" : "asc",
                          }))}
                        >
                          <strong>{col.label}</strong>
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...dialogEventi]
                    .sort((a, b) => {
                      const dir = dialogOrdinamento.direzione === "asc" ? 1 : -1;
                      switch (dialogOrdinamento.campo) {
                        case "titolo":
                          return (a.titolo || "").localeCompare(b.titolo || "", "it") * dir;
                        case "cliente":
                          return ((clientiMap[a.clienteId] || a.clienteNomeStorico || "")
                            .localeCompare(clientiMap[b.clienteId] || b.clienteNomeStorico || "", "it")) * dir;
                        case "data":
                          return ((a.dataInizio || a.data || "").localeCompare(b.dataInizio || b.data || "")) * dir;
                        case "stato":
                          return (a.stato || "").localeCompare(b.stato || "", "it") * dir;
                        case "importo":
                          return ((parseFloat(a.importoVendita) || 0) - (parseFloat(b.importoVendita) || 0)) * dir;
                        default:
                          return 0;
                      }
                    })
                    .map((e) => (
                      <TableRow key={e.id} hover sx={{ cursor: "pointer" }}
                        onClick={() => { setDialogOpen(false); navigate(`/evento/${e.id}`); }}>
                        <TableCell>{e.titolo || "—"}</TableCell>
                        <TableCell>{clientiMap[e.clienteId] || e.clienteNomeStorico || "—"}</TableCell>
                        <TableCell>{e.dataInizio || e.data || "—"}</TableCell>
                        <TableCell><Chip label={e.stato} size="small" /></TableCell>
                        <TableCell align="right">
                          {e.importoVendita ? `€ ${parseFloat(e.importoVendita).toLocaleString("it-IT")}` : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}